import { useState, useEffect, useRef } from 'react';
import { uniqBy } from 'lodash';
import { differenceInMinutes } from 'date-fns';
import * as S from './transportation-station.styled';

import { MAX_DEPARTURES_DISPLAYED_COUNT } from '../constants';
import { getTransportationIcon } from '../utils';

import { TransitBoard } from 'api/get-transit';

interface TransportationStationProps {
  data: TransitBoard;
}

export const TransportationStation = ({ data }: TransportationStationProps) => {
  const departuresRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(false);
  const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    const element = departuresRef.current;
    if (!element) return;
    
    // Check if content needs scrolling
    const checkScrollNeeded = () => {
      const diff = element.scrollHeight - element.clientHeight;
      const percentage = diff / element.scrollHeight;
      // if more than 15% of the content is not visible, we need to scroll
      return percentage > 0.15;
    };
    
    // Set up scrolling
    const setupScroll = () => {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
      }
      
      if (!checkScrollNeeded() || isPaused) return;
      
      let scrollingDown = true;
      let position = 0;
      
      scrollIntervalRef.current = setInterval(() => {
        if (scrollingDown) {
          // Scroll down until we reach the bottom
          position += 1;
          if (position >= element.scrollHeight - element.clientHeight) {
            setTimeout(() => {
              scrollingDown = false;
            }, 2000);
          }
        } else {
          // Scroll back up
          position -= 1;
          if (position <= 0) {
            setTimeout(() => {
              scrollingDown = true;
            }, 2000);
          }
        }
        
        element.scrollTop = position;
      }, 200);
    };
    
    setupScroll();
    
    const handleResize = () => {
      checkScrollNeeded();
      setupScroll();
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [data.departures, isPaused]);
  
  return (
    <S.Container>
      <S.Header>
        <S.Title>{data.place.name}</S.Title>
        <S.Wrapper>
          {uniqBy(data.departures, 'transport.mode').map((departure) => getTransportationIcon(departure.transport.mode))}
        </S.Wrapper>
      </S.Header>
      <S.Subtitle>Arrives (min)</S.Subtitle>
      <S.Departures
        ref={departuresRef}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {!data?.departures?.length 
          ? <S.DepartureText>No available departures</S.DepartureText> 
          : data.departures.slice(0, MAX_DEPARTURES_DISPLAYED_COUNT).map((departure) => (
            <S.Departure key={departure.time}>
              <S.Wrapper>
                <S.DepartureLine bgColor={departure.transport.color} textColor={departure.transport.textColor}>{departure.transport.name}</S.DepartureLine>
                <S.DepartureText>{departure.transport.headsign}</S.DepartureText>
              </S.Wrapper>
              <S.DepartureTime>
                {getTransportationIcon(departure.transport.mode, departure.transport.color)}
                <S.DepartureText>{differenceInMinutes(departure.time, new Date())}</S.DepartureText>
              </S.DepartureTime>
            </S.Departure>
            ))}
      </S.Departures>
    </S.Container>
  );
};